import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';

export type FinanceReadyState = 'live' | 'partial' | 'not-ready';

export function financeReadyState(
  availability?: FinanceChannelFieldAvailability,
): FinanceReadyState {
  if (availability == null || availability === 'AVAILABLE') return 'live';
  if (availability === 'INCOMPLETE_FEES') return 'partial';
  return 'not-ready';
}

function defaultReason(state: FinanceReadyState): string {
  if (state === 'partial') {
    return 'Partial — some figures here are incomplete or missing source data.';
  }
  return 'Not ready — this figure is not wired yet.';
}

type HintCoords = { top: number; left: number; place: 'above' | 'below' };

function coordsForTrigger(el: HTMLElement): HintCoords {
  const rect = el.getBoundingClientRect();
  const place: HintCoords['place'] =
    window.innerHeight - rect.bottom < 88 ? 'above' : 'below';
  const half = 144;
  return {
    top: place === 'below' ? rect.bottom + 6 : rect.top - 6,
    left: Math.min(Math.max(rect.left + rect.width / 2, half), window.innerWidth - half),
    place,
  };
}

/** Hover / keyboard hint for Partial and Not ready chips. Portaled so overflow-hidden cards do not clip it. */
function StatusHint({
  reason,
  label,
  className,
  children,
}: {
  reason: string;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const tooltipId = useId();
  const hideTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const [coords, setCoords] = useState<HintCoords | null>(null);
  const open = coords != null;

  function clearHide() {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }

  function show(el: HTMLElement) {
    clearHide();
    setCoords(coordsForTrigger(el));
  }

  function hide() {
    clearHide();
    hideTimer.current = window.setTimeout(() => {
      setCoords(null);
      hideTimer.current = null;
    }, 120);
  }

  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      clearHide();
      setCoords(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  useEffect(() => () => clearHide(), []);

  return (
    <>
      <button
        type="button"
        className={cn(
          'inline-flex cursor-help appearance-none rounded-md border-0 bg-transparent p-0 align-middle text-inherit focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          className,
        )}
        aria-label={`${label}. ${reason}`}
        onMouseEnter={(event: MouseEvent<HTMLButtonElement>) => show(event.currentTarget)}
        onMouseLeave={hide}
        onFocus={(event: FocusEvent<HTMLButtonElement>) => show(event.currentTarget)}
        onBlur={hide}
      >
        {children}
      </button>
      {open
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className="fixed z-50 max-w-72 rounded-md border border-border bg-popover px-2.5 py-1.5 text-left text-[11px] font-normal normal-case leading-4 tracking-normal text-popover-foreground shadow-md"
              style={{
                top: coords.top,
                left: coords.left,
                transform:
                  coords.place === 'below' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
              }}
              onMouseEnter={clearHide}
              onMouseLeave={hide}
            >
              {reason}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

/** Compact status chip for Finance module headers / rows. Hover explains why. */
export function NotReadyBadge({
  state = 'not-ready',
  className,
  label,
  reason,
}: {
  state?: FinanceReadyState;
  className?: string;
  /** Override default label text. */
  label?: string;
  /** Hover / focus annotation explaining why the figure is partial or not ready. */
  reason?: string;
}) {
  if (state === 'live') return null;

  const text = label ?? (state === 'partial' ? 'Partial' : 'Not ready');

  return (
    <StatusHint label={text} reason={reason ?? defaultReason(state)}>
      <span
        className={cn(
          badgeVariants({ variant: 'outline' }),
          'text-[10px] font-medium uppercase tracking-wide underline decoration-dotted underline-offset-2',
          state === 'partial'
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300 decoration-amber-300/70'
            : 'border-sky-500/40 bg-sky-500/10 text-sky-300 decoration-sky-300/70',
          className,
        )}
      >
        {text}
      </span>
    </StatusHint>
  );
}

/** Inline label used next to incomplete nested figures. Hover explains why. */
export function NotReadyInline({
  className,
  reason,
}: {
  className?: string;
  reason?: string;
}) {
  return (
    <StatusHint
      label="Not ready"
      reason={reason ?? defaultReason('not-ready')}
      className={cn(
        'ml-1.5 text-[10px] font-medium uppercase tracking-wide text-sky-400/90 underline decoration-dotted decoration-sky-400/70 underline-offset-2',
        className,
      )}
    >
      Not ready
    </StatusHint>
  );
}

/** Card/module footnote explaining why figures are missing. */
export function NotReadyNote({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={cn('text-[11px] leading-4 text-sky-400/90', className)}>{children}</p>
  );
}

/** Keep card height while a page-level filter refetch runs. */
export function FinanceRefreshing({
  isLoading,
  children,
  className,
}: {
  isLoading?: boolean | undefined;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative', className)} aria-busy={isLoading || undefined}>
      <div className={cn(isLoading && 'opacity-40')}>{children}</div>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
          <span className="sr-only">Updating</span>
        </div>
      ) : null}
    </div>
  );
}
